import 'flex-render/css'

import liff from '@line/liff'
import { useStore } from '@tanstack/react-form'
import { createFileRoute } from '@tanstack/react-router'
import { postApiV1WeddingsByWeddingIdActivitiesBlessings } from '@wedding-tech/api-client'
import { Button } from '@wedding-tech/ui/button'
import { Card, CardContent } from '@wedding-tech/ui/card'
import { Label } from '@wedding-tech/ui/label'
import confetti from 'canvas-confetti'
import { render } from 'flex-render'
import parseTemplate from 'json-templates'
import { domAnimation, LazyMotion, m } from 'motion/react'
import { useEffect, useState } from 'react'
import { z } from 'zod'

import { useSettingsContext } from '@/contexts/SettingsContext'
import { useAppForm } from '@/lib/forms/hook'
import { useLIFF } from '@/lib/liff/context'
import { openLineOA } from '@/lib/liff/utils'
import { useEventCopy } from '@/lib/utils/eventCopy'
import MdiHeart from '~icons/mdi/heart'
import MdiImage from '~icons/mdi/image'

import flexBlessingTemplate from '../../../../../../data/flex-blessing.json'

const BLESSING_IMAGES = [
  'https://storage.googleapis.com/oakoi-wedding/public/pre-wd-01.webp',
  'https://storage.googleapis.com/oakoi-wedding/public/pre-wd-02.webp',
  'https://storage.googleapis.com/oakoi-wedding/public/pre-wd-03.webp',
  'https://storage.googleapis.com/oakoi-wedding/public/pre-wd-04.webp',
  'https://storage.googleapis.com/oakoi-wedding/public/pre-wd-05.webp',
  'https://storage.googleapis.com/oakoi-wedding/public/pre-wd-06.webp',
  'https://storage.googleapis.com/oakoi-wedding/public/pre-wd-07.webp',
  'https://storage.googleapis.com/oakoi-wedding/public/pre-wd-08.webp',
  'https://storage.googleapis.com/oakoi-wedding/public/pre-wd-09.webp',
]

const blessingSearchSchema = z.object({
  room: z.string().optional(),
})

const blessingSchema = z.object({
  senderName: z.string().min(1, 'กรุณาพิมพ์ชื่อผู้ส่ง'),
  message: z.string().min(1, 'กรุณาพิมพ์คำอวยพร'),
})

export const Route = createFileRoute(
  '/e/$eventId/_dashboard/activities/wedding-blessing',
)({
  validateSearch: (search) => blessingSearchSchema.parse(search),
  component: WeddingBlessingPage,
})

function BlessingImage({
  src,
  active,
  onClick,
}: {
  src: string
  active: boolean
  onClick: () => void
}) {
  const [loaded, setLoaded] = useState(false)

  return (
    <button
      type="button"
      onClick={onClick}
      className={`bg-muted/30 relative size-20 shrink-0 overflow-hidden rounded-xl border-2 transition-all ${
        active
          ? 'border-primary ring-muted ring-2 ring-offset-2'
          : 'border-transparent opacity-60 hover:opacity-100'
      }`}
    >
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center text-stone-200">
          <MdiImage className="size-8" />
        </div>
      )}
      <img
        src={src}
        alt="Blessing template"
        className={`size-full object-cover transition-opacity duration-300 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={() => setLoaded(true)}
      />
    </button>
  )
}

function WeddingBlessingPage() {
  const { eventId } = Route.useParams()
  const { room } = Route.useSearch()
  const { profile, isInClient } = useLIFF()
  const { settings } = useSettingsContext()
  const copy = useEventCopy()
  const context = liff.getContext()
  const isUtou = context?.type === 'utou'
  const isValidAccess = !!room && isUtou

  const [selectedImage, setSelectedImage] = useState(BLESSING_IMAGES[0])
  const [flexHtml, setFlexHtml] = useState('')

  const template = parseTemplate(flexBlessingTemplate)

  const form = useAppForm({
    defaultValues: {
      senderName: profile?.displayName || '',
      message: '',
    },
    validators: {
      onChange: blessingSchema,
    },
    onSubmit: async ({ value }) => {
      if (!isValidAccess && isInClient) {
        alert('กรุณาเข้าใช้งานผ่านห้องแชทส่วนตัวเท่านั้น')
        return
      }

      try {
        // Log to API before sending
        const response = await postApiV1WeddingsByWeddingIdActivitiesBlessings({
          path: { weddingId: eventId },
          body: {
            message: value.message.trim(),
            imageUrl: selectedImage,
            userName: value.senderName.trim(),
            userProfileUrl:
              profile?.pictureUrl ||
              'https://storage.googleapis.com/oakoi-wedding/public/line-avatar-chat.webp',
          },
        })

        const blessingId = response.data?.id

        const flexMessage = template({
          message: value.message.trim(),
          imageUrl: selectedImage,
          userName: value.senderName.trim(),
          userProfileUrl:
            profile?.pictureUrl ||
            'https://storage.googleapis.com/oakoi-wedding/public/line-avatar-chat.webp',
          blessingId,
        }) as any

        // Add identification tag to altText
        flexMessage.altText = `${value.senderName.trim()} ส่งคำอวยพร`

        if (isInClient) {
          await liff.sendMessages([
            flexMessage,
            {
              type: 'text',
              text: `ส่งคำอวยพรเรียบร้อยแล้ว! 💖 [Blessing:${blessingId}]`,
            },
          ])

          confetti({
            particleCount: 150,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#D8959B', '#829672', '#F2D1D4', '#344C3D'],
          })

          setTimeout(() => {
            liff.closeWindow()
          }, 2000)
        } else {
          console.log('Sending message:', flexMessage)
          confetti({
            particleCount: 150,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#D8959B', '#829672', '#F2D1D4', '#344C3D'],
          })
          alert('ส่งคำอวยพรเรียบร้อยแล้ว (Browser Mock)')
          form.reset()
        }
      } catch (error) {
        console.error('Failed to send message:', error)
        alert('เกิดข้อผิดพลาดในการส่งคำอวยพร กรุณาลองใหม่อีกครั้ง')
      }
    },
  })

  const message = useStore(form.store, (s) => s.values.message)
  const senderName = useStore(form.store, (s) => s.values.senderName)

  useEffect(() => {
    if (profile?.displayName) {
      form.setFieldValue('senderName', profile.displayName)
    }
  }, [profile?.displayName, form])

  useEffect(() => {
    const flexMessage = template({
      message: message || '‎',
      imageUrl: selectedImage,
      userName: senderName || profile?.displayName || 'Guest',
      userProfileUrl:
        profile?.pictureUrl ||
        'https://firebasestorage.googleapis.com/v0/b/oakoi-wedding/o/public%2Fline-avatar-chat.webp?alt=media',
    }) as any
    console.log('flexConten', flexMessage.contents)
    setFlexHtml(render(flexMessage.contents))
  }, [message, senderName, selectedImage, profile, template])

  return (
    <LazyMotion features={domAnimation}>
      <div className="bg-card text-foreground flex min-h-full flex-col items-center p-4 pb-32 sm:p-8 sm:pb-8">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 w-full max-w-md space-y-8"
        >
          <div className="space-y-2 text-center">
            <m.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 12, stiffness: 200 }}
              className="bg-background mx-auto flex size-20 items-center justify-center rounded-full shadow-lg"
            >
              <MdiHeart className="text-primary size-10" />
            </m.div>
            <h1 className="text-foreground text-4xl font-bold tracking-tight">
              {copy.blessingTitle}
            </h1>
            <p className="text-muted-foreground font-medium">
              {copy.blessingSubtitle}
            </p>
          </div>

          <Card className="overflow-hidden border-none bg-white/50 pt-0 backdrop-blur-sm">
            <CardContent className="p-0">
              {!isValidAccess && isInClient ? (
                <div className="space-y-4 bg-amber-50 p-6 text-center shadow-inner">
                  <div className="flex justify-center">
                    <div className="flex size-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                      <MdiHeart className="size-6" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-lg font-bold text-amber-900">
                      ต้องเข้าใช้งานผ่านแชทส่วนตัว
                    </p>
                    <p className="text-sm leading-relaxed text-amber-700">
                      เพื่อความเป็นส่วนตัวและความปลอดภัย
                      กรุณาเข้าใช้งานผ่านห้องแชทของ OAKOI WEDDING
                      เพื่อให้ระบบสามารถระบุตัวตนและส่งคำอวยพรของคุณได้
                    </p>
                  </div>
                  <Button
                    onClick={() =>
                      openLineOA((settings as any)?.lineOaBasicId || '')
                    }
                    className="w-full rounded-full bg-amber-600 font-bold text-white hover:bg-amber-700"
                  >
                    เปิดห้องแชท LINE OA
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col p-6">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      form.handleSubmit()
                    }}
                    className="space-y-8"
                  >
                    <form.AppField name="senderName">
                      {(field) => (
                        <field.TextField
                          label="ชื่อผู้ส่ง *"
                          placeholder="พิมพ์ชื่อของคุณที่นี่..."
                          className="focus:ring-primary border-border bg-background/50 rounded-2xl p-4 text-lg"
                        />
                      )}
                    </form.AppField>

                    <form.AppField name="message">
                      {(field) => (
                        <field.TextareaField
                          label="เขียนคำอวยพร *"
                          placeholder="พิมพ์คำอวยพรที่นี่..."
                          className="focus:ring-primary border-border bg-background/50 min-h-32 resize-none rounded-2xl p-4 text-lg"
                        />
                      )}
                    </form.AppField>

                    <div className="space-y-3">
                      <label className="text-muted-foreground text-sm font-bold">
                        เลือกรูปภาพ *
                      </label>
                      <div className="no-scrollbar flex gap-3 overflow-x-auto py-2 pl-1">
                        {BLESSING_IMAGES.map((img) => (
                          <BlessingImage
                            key={img}
                            src={img}
                            active={selectedImage === img}
                            onClick={() => setSelectedImage(img)}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label className="text-muted-foreground text-sm font-bold">
                        แสดงตัวอย่าง
                      </Label>
                      <div className="bg-background ring-border/50 relative flex justify-center rounded-3xl p-8 shadow-inner ring-1">
                        <div className="text-muted/50 absolute inset-0 flex items-center justify-center">
                          <MdiImage className="size-20" />
                        </div>
                        <div
                          className="relative z-10 w-full max-w-75"
                          dangerouslySetInnerHTML={{ __html: flexHtml }}
                        />
                      </div>
                    </div>

                    <form.AppForm>
                      <form.SubmitButton
                        label="ส่งคำอวยพร"
                        loadingLabel="กำลังส่ง..."
                        className="bg-primary w-full rounded-full py-8 text-2xl font-bold text-white shadow-xl transition-all duration-300 hover:opacity-90 active:scale-95 disabled:bg-stone-200"
                      />
                    </form.AppForm>
                  </form>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-muted-foreground text-center text-xs">
            ข้อความจะถูกส่งไปยังแชทของคุณ
          </p>
        </m.div>
      </div>
    </LazyMotion>
  )
}
